import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactSelect from "react-select";

const today = () => new Date().toISOString().slice(0, 10);

const projectTypes = [
  ["panel_building", "Panel Building"],
  ["gen_synchronizing", "Gen Synchronizing"],
  ["installation", "Installation"],
  ["retrofit", "Retrofit"],
  ["service", "Service"],
  ["other", "Other Electrical Work"]
];

const standardTypes = ["panel_building", "gen_synchronizing", "installation", "retrofit", "service"];

const statuses = [
  ["draft", "Draft"],
  ["review", "Review"],
  ["approved", "Approved"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"]
];

const formatUnit = (value = "") => {
  const unit = String(value || "").trim();
  if (!unit || unit.toLowerCase() === "pcs" || unit.toLowerCase() === "unit") return "No";
  return unit.charAt(0).toUpperCase() + unit.slice(1).toLowerCase();
};

const emptyExpenseLine = (name = "", unit = "No") => ({
  name,
  supplierName: "",
  description: "",
  remarks: "",
  quantity: "",
  unit,
  unitRate: "",
  amount: "",
  overheadType: "none",
  overheadValue: "",
  overheadAmount: "",
  lineTotal: ""
});

const emptyLabourLine = (role = "") => ({
  role,
  supplierName: "",
  remarks: "",
  workers: "",
  days: "",
  ratePerDay: "",
  amount: ""
});

const emptyMetalLine = (metal = "copper", defaultRate = "") => ({
  metal,
  supplierName: "",
  remarks: "",
  quantity: "1",
  runs: "1",
  widthMm: "",
  thicknessMm: "",
  lengthMm: "",
  kgUsed: "",
  ratePerKg: defaultRate || "",
  amount: "",
  overheadType: "none",
  overheadValue: "",
  overheadAmount: "",
  lineTotal: ""
});

const emptyForm = () => ({
  estimateNo: "",
  estimateDate: today(),
  projectName: "",
  projectType: "panel_building",
  customerId: "",
  customerName: "",
  location: "",
  referenceNo: "",
  status: "draft",
  copperRate: "1350",
  aluminiumRate: "400",
  products: [
    emptyExpenseLine("Panel enclosure", "set"),
    emptyExpenseLine("Switchgear / breakers", "No"),
    emptyExpenseLine("Controller / relay / meter", "No")
  ],
  materials: [
    emptyExpenseLine("Control wire / power cable", "meter"),
    emptyExpenseLine("Lugs, ferrules, glands", "lot"),
    emptyExpenseLine("Busbar supports and hardware", "lot")
  ],
  metals: [
    emptyMetalLine("copper", "1350"),
    emptyMetalLine("aluminium", "400")
  ],
  labour: [
    emptyLabourLine("Panel wiring"),
    emptyLabourLine("Testing and commissioning")
  ],
  transport: [emptyExpenseLine("Vehicle / freight", "trip")],
  stayExpenses: [emptyExpenseLine("Stay / lodging", "night")],
  otherExpenses: [emptyExpenseLine("Site tools / consumables", "lot")],
  productMultiplier: "1",
  materialMultiplier: "1",
  metalMultiplier: "1",
  labourMultiplier: "1",
  transportMultiplier: "1",
  stayMultiplier: "1",
  otherMultiplier: "1",
  overheadPercent: "5",
  contingencyPercent: "3",
  profitPercent: "15",
  gstPercent: "18",
  notes: ""
});

const formatCurrency = (value) => Number(value || 0).toLocaleString("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2
});

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB");
};

const toNumber = (value) => Number(value) || 0;
const roundMoney = (value) => Number(toNumber(value).toFixed(2));
const roundWeight = (value) => Number(toNumber(value).toFixed(3));
const METAL_WEIGHT_FACTORS = {
  copper: 0.00000896,
  aluminium: 0.0000027
};

const computeMetalWeightKg = (line) => {
  const quantity = toNumber(line.quantity || 1);
  const runs = toNumber(line.runs || 1);
  const widthMm = toNumber(line.widthMm);
  const thicknessMm = toNumber(line.thicknessMm);
  const lengthMm = toNumber(line.lengthMm);

  if (widthMm > 0 && thicknessMm > 0 && lengthMm > 0) {
    const factor = METAL_WEIGHT_FACTORS[line.metal === "aluminium" ? "aluminium" : "copper"];
    return roundWeight(widthMm * thicknessMm * lengthMm * factor * quantity * runs);
  }

  return roundWeight(line.kgUsed);
};

const computeLineAmount = (line) => {
  const quantity = toNumber(line.quantity);
  const unitRate = toNumber(line.unitRate);
  if (quantity > 0 && unitRate > 0) return roundMoney(quantity * unitRate);
  const amount = toNumber(line.amount);
  if (amount > 0) return roundMoney(amount);
  return 0;
};

const computeLabourAmount = (line) => {
  const amount = toNumber(line.amount);
  if (amount > 0) return roundMoney(amount);
  return roundMoney(toNumber(line.workers) * toNumber(line.days) * toNumber(line.ratePerDay));
};

const computeMetalAmount = (line) => {
  const kgUsed = computeMetalWeightKg(line);
  const ratePerKg = toNumber(line.ratePerKg);
  if (kgUsed > 0 && ratePerKg > 0) return roundMoney(kgUsed * ratePerKg);
  const amount = toNumber(line.amount);
  if (amount > 0) return roundMoney(amount);
  return 0;
};

const sumExpenseLines = (lines) => lines.reduce((sum, line) => sum + computeLineAmount(line), 0);
const sumLabourLines = (lines) => lines.reduce((sum, line) => sum + computeLabourAmount(line), 0);
const sumMetalLines = (lines) => lines.reduce((sum, line) => sum + computeMetalAmount(line), 0);

const mapExpenseLineForApi = (line) => ({
  ...line,
  quantity: toNumber(line.quantity || 1),
  unitRate: toNumber(line.unitRate),
  amount: computeLineAmount(line),
  overheadType: "none",
  overheadValue: 0,
  overheadAmount: 0,
  lineTotal: computeLineAmount(line)
});

const mapLabourLineForApi = (line) => ({
  ...line,
  workers: toNumber(line.workers || 1),
  days: toNumber(line.days || 1),
  ratePerDay: toNumber(line.ratePerDay),
  amount: computeLabourAmount(line)
});

const mapMetalLineForApi = (line) => ({
  ...line,
  quantity: toNumber(line.quantity || 1),
  runs: toNumber(line.runs || 1),
  widthMm: toNumber(line.widthMm),
  thicknessMm: toNumber(line.thicknessMm),
  lengthMm: toNumber(line.lengthMm),
  kgUsed: computeMetalWeightKg(line),
  ratePerKg: toNumber(line.ratePerKg),
  amount: computeMetalAmount(line),
  overheadType: "none",
  overheadValue: 0,
  overheadAmount: 0,
  lineTotal: computeMetalAmount(line)
});

export default function ProjectExpenses() {
  const adminName = localStorage.getItem("adminName") || localStorage.getItem("accountsName") || "";
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const customerOptions = useMemo(() => {
    return customers.map((c) => ({
      value: c._id,
      label: c.name || c.customerId || "",
      data: c
    }));
  }, [customers]);

  const selectedCustomerOption = useMemo(() => {
    if (!form.customerId) {
      const matched = customers.find(
        (c) => (c.name || c.customerId || "") === form.customerName
      );
      if (matched) return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
      return form.customerName ? { value: "", label: form.customerName } : null;
    }
    const matched = customers.find((c) => c._id === form.customerId);
    if (matched) {
      return { value: matched._id, label: matched.name || matched.customerId || "", data: matched };
    }
    return form.customerName ? { value: form.customerId, label: form.customerName } : null;
  }, [customers, form.customerId, form.customerName]);

  const reactSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "44px",
      borderRadius: "0.5rem",
      borderColor: state.isFocused ? "#047857" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 1px #047857" : "none",
      fontSize: "0.875rem",
      backgroundColor: "#ffffff",
      "&:hover": {
        borderColor: "#047857"
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8"
    }),
    menu: (base) => ({
      ...base,
      zIndex: 60,
      borderRadius: "0.5rem",
      overflow: "hidden",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 60
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#d1fae5"
        : state.isFocused
          ? "#ecfdf5"
          : "#ffffff",
      color: "#064e3b",
      fontSize: "0.875rem"
    }),
    indicatorSeparator: () => ({
      display: "none"
    })
  };

  const loadData = async (query = "") => {
    try {
      setLoading(true);
      const [customersRes, suppliersRes, expensesRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/customers").catch(err => { console.error("Failed to load customers", err); return { data: [] }; }),
        axios.get(API_BASE_URL + "/api/dealers").catch(err => { console.error("Failed to load dealers", err); return { data: [] }; }),
        axios.get(API_BASE_URL + "/api/project-expenses", {
          params: query.trim() ? { search: query.trim() } : undefined
        }).catch(err => { console.error("Failed to load project expenses", err); return { data: [] }; })
      ]);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);
      setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);
    } catch (error) {
      console.error("Failed to load project expenses reference data", error);
      setCustomers([]);
      setSuppliers([]);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const totals = useMemo(() => {
    const productMultiplier = Math.max(1, toNumber(form.productMultiplier || 1));
    const materialMultiplier = Math.max(1, toNumber(form.materialMultiplier || 1));
    const metalMultiplier = Math.max(1, toNumber(form.metalMultiplier || 1));
    const labourMultiplier = Math.max(1, toNumber(form.labourMultiplier || 1));
    const transportMultiplier = Math.max(1, toNumber(form.transportMultiplier || 1));
    const stayMultiplier = Math.max(1, toNumber(form.stayMultiplier || 1));
    const otherMultiplier = Math.max(1, toNumber(form.otherMultiplier || 1));

    const productBaseTotal = roundMoney(sumExpenseLines(form.products));
    const productTotal = roundMoney(productBaseTotal * productMultiplier);
    const materialBaseTotal = roundMoney(sumExpenseLines(form.materials));
    const materialTotal = roundMoney(materialBaseTotal * materialMultiplier);
    const metalBaseTotal = roundMoney(sumMetalLines(form.metals));
    const metalTotal = roundMoney(metalBaseTotal * metalMultiplier);
    const labourBaseTotal = roundMoney(sumLabourLines(form.labour));
    const labourTotal = roundMoney(labourBaseTotal * labourMultiplier);
    const transportBaseTotal = roundMoney(sumExpenseLines(form.transport));
    const transportTotal = roundMoney(transportBaseTotal * transportMultiplier);
    const stayBaseTotal = roundMoney(sumExpenseLines(form.stayExpenses));
    const stayTotal = roundMoney(stayBaseTotal * stayMultiplier);
    const otherBaseTotal = roundMoney(sumExpenseLines(form.otherExpenses));
    const otherTotal = roundMoney(otherBaseTotal * otherMultiplier);
    const directCost = roundMoney(productTotal + materialTotal + metalTotal + labourTotal + transportTotal + stayTotal + otherTotal);
    const overheadAmount = roundMoney(directCost * toNumber(form.overheadPercent) / 100);
    const contingencyAmount = roundMoney(directCost * toNumber(form.contingencyPercent) / 100);
    const costBeforeProfit = roundMoney(directCost + overheadAmount + contingencyAmount);
    const profitAmount = roundMoney(costBeforeProfit * toNumber(form.profitPercent) / 100);
    const sellingPriceBeforeTax = roundMoney(costBeforeProfit + profitAmount);
    const gstAmount = roundMoney(sellingPriceBeforeTax * toNumber(form.gstPercent) / 100);

    return {
      productTotal,
      productBaseTotal,
      productOverheadTotal: 0,
      materialBaseTotal,
      materialOverheadTotal: 0,
      materialTotal,
      metalBaseTotal,
      metalOverheadTotal: 0,
      metalTotal,
      labourBaseTotal,
      labourTotal,
      transportBaseTotal,
      transportTotal,
      stayBaseTotal,
      stayTotal,
      otherBaseTotal,
      otherTotal,
      directCost,
      overheadAmount,
      contingencyAmount,
      costBeforeProfit,
      profitAmount,
      sellingPriceBeforeTax,
      gstAmount,
      finalProjectValue: roundMoney(sellingPriceBeforeTax + gstAmount)
    };
  }, [form]);

  const applyCustomer = (value) => {
    const matched = customers.find((customer) =>
      [customer.name, customer.customerId]
        .filter(Boolean)
        .some((entry) => entry.toLowerCase() === value.trim().toLowerCase())
    );

    setForm((current) => ({
      ...current,
      customerName: value,
      customerId: matched?._id || ""
    }));
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    if (name === "customerName") {
      applyCustomer(value);
      return;
    }
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateExpenseLine = (section, index, field, value) => {
    setForm((current) => ({
      ...current,
      [section]: current[section].map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const next = { ...line, [field]: value };
        if (field === "quantity" || field === "unitRate" || field === "amount") {
          const quantity = toNumber(field === "quantity" ? value : next.quantity);
          const unitRate = toNumber(field === "unitRate" ? value : next.unitRate);
          const baseAmount = field === "amount"
            ? roundMoney(value)
            : quantity > 0 && unitRate > 0
              ? roundMoney(quantity * unitRate)
              : field === "quantity" || field === "unitRate"
                ? roundMoney(quantity * unitRate)
                : computeLineAmount(next);
          next.amount = String(baseAmount);
          next.lineTotal = String(baseAmount);
        }
        return next;
      })
    }));
  };

  const handleCopperRateChange = (rate) => {
    setForm((current) => ({
      ...current,
      copperRate: rate,
      metals: current.metals.map((line) => {
        if (line.metal === "copper") {
          const next = { ...line, ratePerKg: rate };
          const kgUsed = computeMetalWeightKg(next);
          const rateVal = toNumber(rate);
          const baseAmount = kgUsed > 0 && rateVal > 0 ? roundMoney(kgUsed * rateVal) : computeMetalAmount(next);
          next.amount = String(baseAmount);
          next.lineTotal = String(baseAmount);
          return next;
        }
        return line;
      })
    }));
  };

  const handleAluminiumRateChange = (rate) => {
    setForm((current) => ({
      ...current,
      aluminiumRate: rate,
      metals: current.metals.map((line) => {
        if (line.metal === "aluminium") {
          const next = { ...line, ratePerKg: rate };
          const kgUsed = computeMetalWeightKg(next);
          const rateVal = toNumber(rate);
          const baseAmount = kgUsed > 0 && rateVal > 0 ? roundMoney(kgUsed * rateVal) : computeMetalAmount(next);
          next.amount = String(baseAmount);
          next.lineTotal = String(baseAmount);
          return next;
        }
        return line;
      })
    }));
  };

  const updateMetalLine = (index, field, value) => {
    setForm((current) => ({
      ...current,
      metals: current.metals.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const next = { ...line, [field]: value };
        if (field === "metal") {
          const defaultRate = value === "aluminium" ? (current.aluminiumRate || "400") : (current.copperRate || "1350");
          next.ratePerKg = defaultRate;
        }
        if (["metal", "quantity", "runs", "widthMm", "thicknessMm", "lengthMm", "kgUsed", "ratePerKg", "amount"].includes(field)) {
          const kgUsed = computeMetalWeightKg(next);
          const ratePerKg = toNumber(next.ratePerKg);
          next.kgUsed = kgUsed > 0 ? String(kgUsed) : "";
          const baseAmount = field === "amount"
            ? roundMoney(value)
            : kgUsed > 0 && ratePerKg > 0
              ? roundMoney(kgUsed * ratePerKg)
              : computeMetalAmount(next);
          next.amount = String(baseAmount);
          next.lineTotal = String(baseAmount);
        }
        return next;
      })
    }));
  };

  const updateLabourLine = (index, field, value) => {
    setForm((current) => ({
      ...current,
      labour: current.labour.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const next = { ...line, [field]: value };
        if (["workers", "days", "ratePerDay"].includes(field)) {
          const workers = toNumber(field === "workers" ? value : next.workers);
          const days = toNumber(field === "days" ? value : next.days);
          const ratePerDay = toNumber(field === "ratePerDay" ? value : next.ratePerDay);
          next.amount = workers > 0 && days > 0 && ratePerDay > 0 ? String(roundMoney(workers * days * ratePerDay)) : "";
        }
        return next;
      })
    }));
  };

  const addExpenseLine = (section, line = emptyExpenseLine()) => {
    setForm((current) => ({ ...current, [section]: [...current[section], line] }));
  };

  const removeExpenseLine = (section, index, fallback = emptyExpenseLine()) => {
    setForm((current) => ({
      ...current,
      [section]: current[section].length === 1 ? [fallback] : current[section].filter((_, lineIndex) => lineIndex !== index)
    }));
  };

  const addMetalLine = () => {
    setForm((current) => ({
      ...current,
      metals: [...current.metals, emptyMetalLine("copper", current.copperRate || "1350")]
    }));
  };

  const removeMetalLine = (index) => {
    setForm((current) => ({
      ...current,
      metals: current.metals.length === 1
        ? [emptyMetalLine("copper", current.copperRate || "1350")]
        : current.metals.filter((_, lineIndex) => lineIndex !== index)
    }));
  };

  const moveExpenseLine = (section, index, direction) => {
    setForm((current) => {
      const list = [...(current[section] || [])];
      if (direction === "up" && index > 0) {
        const temp = list[index];
        list[index] = list[index - 1];
        list[index - 1] = temp;
      } else if (direction === "down" && index < list.length - 1) {
        const temp = list[index];
        list[index] = list[index + 1];
        list[index + 1] = temp;
      }
      return { ...current, [section]: list };
    });
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId("");
  };

  const openFreshForm = () => {
    resetForm();
    setShowForm(true);
  };

  const submitExpense = async (event) => {
    event.preventDefault();
    const isEditing = Boolean(editingId);

    if (!form.projectName.trim()) {
      alert("Enter project name");
      return;
    }

    if (totals.directCost <= 0) {
      alert("Add at least one expense amount");
      return;
    }

    const payload = {
      ...form,
      createdBy: adminName,
      copperRate: toNumber(form.copperRate),
      aluminiumRate: toNumber(form.aluminiumRate),
      products: form.products.map(mapExpenseLineForApi),
      materials: form.materials.map(mapExpenseLineForApi),
      metals: form.metals.map(mapMetalLineForApi),
      labour: form.labour.map(mapLabourLineForApi),
      transport: form.transport.map(mapExpenseLineForApi),
      stayExpenses: form.stayExpenses.map(mapExpenseLineForApi),
      otherExpenses: form.otherExpenses.map(mapExpenseLineForApi),
      productMultiplier: toNumber(form.productMultiplier || 1),
      materialMultiplier: toNumber(form.materialMultiplier || 1),
      metalMultiplier: toNumber(form.metalMultiplier || 1),
      labourMultiplier: toNumber(form.labourMultiplier || 1),
      transportMultiplier: toNumber(form.transportMultiplier || 1),
      stayMultiplier: toNumber(form.stayMultiplier || 1),
      otherMultiplier: toNumber(form.otherMultiplier || 1),
      overheadPercent: toNumber(form.overheadPercent),
      contingencyPercent: toNumber(form.contingencyPercent),
      profitPercent: toNumber(form.profitPercent),
      gstPercent: toNumber(form.gstPercent)
    };

    try {
      setSaving(true);
      if (isEditing) {
        await axios.put(API_BASE_URL + `/api/project-expenses/${editingId}`, payload);
      } else {
        await axios.post(API_BASE_URL + "/api/project-expenses", payload);
      }
      await loadData(search);
      if (!isEditing) {
        openFreshForm();
      } else {
        setShowForm(true);
      }
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save project estimate");
    } finally {
      setSaving(false);
    }
  };

  const editExpense = (expense) => {
    setEditingId(expense._id);
    setShowForm(true);
    setForm({
      ...emptyForm(),
      ...expense,
      copperRate: String(expense.copperRate || "1350"),
      aluminiumRate: String(expense.aluminiumRate || "400"),
      estimateDate: expense.estimateDate ? new Date(expense.estimateDate).toISOString().slice(0, 10) : today(),
      products: expense.products?.length ? expense.products.map(normalizeExpenseLineForForm) : [emptyExpenseLine()],
      materials: expense.materials?.length ? expense.materials.map(normalizeExpenseLineForForm) : [emptyExpenseLine()],
      metals: expense.metals?.length ? expense.metals.map(normalizeMetalLineForForm) : emptyForm().metals,
      labour: expense.labour?.length ? expense.labour.map(normalizeLabourLineForForm) : [emptyLabourLine()],
      transport: expense.transport?.length ? expense.transport.map(normalizeExpenseLineForForm) : [emptyExpenseLine("", "trip")],
      stayExpenses: expense.stayExpenses?.length ? expense.stayExpenses.map(normalizeExpenseLineForForm) : [emptyExpenseLine("Stay / lodging", "night")],
      otherExpenses: expense.otherExpenses?.length ? expense.otherExpenses.map(normalizeExpenseLineForForm) : [emptyExpenseLine()],
      productMultiplier: String(expense.productMultiplier ?? 1),
      materialMultiplier: String(expense.materialMultiplier ?? 1),
      metalMultiplier: String(expense.metalMultiplier ?? 1),
      labourMultiplier: String(expense.labourMultiplier ?? 1),
      transportMultiplier: String(expense.transportMultiplier ?? 1),
      stayMultiplier: String(expense.stayMultiplier ?? 1),
      otherMultiplier: String(expense.otherMultiplier ?? 1),
      overheadPercent: String(expense.overheadPercent ?? 0),
      contingencyPercent: String(expense.contingencyPercent ?? 0),
      profitPercent: String(expense.profitPercent ?? 0),
      gstPercent: String(expense.gstPercent ?? 18)
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicateExpense = (expense) => {
    setEditingId("");
    setShowForm(true);
    const { _id, id, __v, createdAt, updatedAt, estimateNo, ...rest } = expense;
    setForm({
      ...emptyForm(),
      ...rest,
      estimateNo: "",
      estimateDate: today(),
      projectName: rest.projectName ? `${rest.projectName} (Copy)` : "",
      status: "draft",
      copperRate: String(rest.copperRate || "1350"),
      aluminiumRate: String(rest.aluminiumRate || "400"),
      products: rest.products?.length ? rest.products.map(normalizeExpenseLineForForm) : [emptyExpenseLine()],
      materials: rest.materials?.length ? rest.materials.map(normalizeExpenseLineForForm) : [emptyExpenseLine()],
      metals: rest.metals?.length ? rest.metals.map(normalizeMetalLineForForm) : emptyForm().metals,
      labour: rest.labour?.length ? rest.labour.map(normalizeLabourLineForForm) : [emptyLabourLine()],
      transport: rest.transport?.length ? rest.transport.map(normalizeExpenseLineForForm) : [emptyExpenseLine("", "trip")],
      stayExpenses: rest.stayExpenses?.length ? rest.stayExpenses.map(normalizeExpenseLineForForm) : [emptyExpenseLine("Stay / lodging", "night")],
      otherExpenses: rest.otherExpenses?.length ? rest.otherExpenses.map(normalizeExpenseLineForForm) : [emptyExpenseLine()],
      productMultiplier: String(rest.productMultiplier ?? 1),
      materialMultiplier: String(rest.materialMultiplier ?? 1),
      metalMultiplier: String(rest.metalMultiplier ?? 1),
      labourMultiplier: String(rest.labourMultiplier ?? 1),
      transportMultiplier: String(rest.transportMultiplier ?? 1),
      stayMultiplier: String(rest.stayMultiplier ?? 1),
      otherMultiplier: String(rest.otherMultiplier ?? 1),
      overheadPercent: String(rest.overheadPercent ?? 0),
      contingencyPercent: String(rest.contingencyPercent ?? 0),
      profitPercent: String(rest.profitPercent ?? 0),
      gstPercent: String(rest.gstPercent ?? 18)
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteExpense = async (expense) => {
    if (!window.confirm(`Delete project estimate ${expense.estimateNo}?`)) return;
    await axios.delete(API_BASE_URL + `/api/project-expenses/${expense._id}`);
    await loadData(search);
  };

  const openExpensePdf = async (expense) => {
    try {
      const response = await axios.get(API_BASE_URL + `/api/project-expenses/${expense._id}/pdf`, {
        responseType: "blob"
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const pdfWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
      if (!pdfWindow) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (error) {
      let message = "Failed to open project expense PDF";
      try {
        const status = error?.response?.status;
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          if (text) {
            const parsed = JSON.parse(text);
            message = parsed?.message || message;
          }
        } else if (data?.message) {
          message = data.message;
        }
        if (status === 403) {
          message = "Access denied for Project Expense PDF";
        }
      } catch (_) {
        // Keep fallback message
      }
      alert(message);
    }
  };

  const downloadExpensePdf = async (expense) => {
    try {
      const response = await axios.get(API_BASE_URL + `/api/project-expenses/${expense._id}/pdf?download=1`, {
        responseType: "blob"
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${expense.estimateNo || "estimate"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (error) {
      console.error(error);
      alert("Failed to download Project Expense PDF");
    }
  };

  return (
    <div className="mobile-dense-page space-y-4 md:space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700 md:text-sm md:tracking-[0.24em]">Project Costing</p>
            <h1 className="mt-2 text-2xl font-bold leading-tight text-slate-950 md:text-3xl">Project Management Costing</h1>
          </div>
          <button
            type="button"
            onClick={() => {
              if (showForm) {
                setShowForm(false);
              } else {
                openFreshForm();
              }
            }}
            className="w-full rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800 sm:w-auto"
          >
            {showForm ? "Hide Form" : "+ Create Project Estimate"}
          </button>
        </div>
      </section>

      {showForm ? (
        <form onSubmit={submitExpense} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:p-5">
          <div className="mb-5 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-950">{editingId ? "Edit Project Estimate" : "Create Project Estimate"}</h2>
            </div>
            <button type="button" onClick={openFreshForm} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">
              Reset
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Input label="Project Name" name="projectName" value={form.projectName} onChange={updateField} required placeholder="DG synchronization panel" />
            <div>
              <Select
                label="Project Type"
                name="projectType"
                value={standardTypes.includes(form.projectType) ? form.projectType : "other"}
                onChange={updateField}
                options={projectTypes}
              />
              {!standardTypes.includes(form.projectType) && (
                <div className="mt-3">
                  <Input
                    label="Specify Project Type"
                    name="projectType"
                    value={form.projectType === "other" ? "" : form.projectType}
                    onChange={updateField}
                    placeholder="Enter custom project type"
                    required
                  />
                </div>
              )}
            </div>
            <Input label="Estimate No" name="estimateNo" value={form.estimateNo} onChange={updateField} placeholder="Auto-generated if empty" />
            <Input label="Estimate Date" name="estimateDate" type="date" value={form.estimateDate} onChange={updateField} />
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Customer</label>
              <ReactSelect
                options={customerOptions}
                value={selectedCustomerOption}
                styles={reactSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                onChange={(option) => {
                  if (option) {
                    setForm((current) => ({
                      ...current,
                      customerName: option.label,
                      customerId: option.value
                    }));
                  } else {
                    setForm((current) => ({
                      ...current,
                      customerName: "",
                      customerId: ""
                    }));
                  }
                }}
                placeholder="Search customer name or ID..."
                isClearable
              />
            </div>
            <Input label="Project Location" name="location" value={form.location} onChange={updateField} />
            <Input label="Reference / PO No" name="referenceNo" value={form.referenceNo} onChange={updateField} />
            <Select label="Status" name="status" value={form.status} onChange={updateField} options={statuses} />
          </div>

          <div className="mt-6 space-y-6">
            <ExpenseTable title="Products and Bought-Out Items" hint="Examples: panel enclosure, ACB/MCCB, contactors, controller, meters." section="products" lines={form.products} total={totals.productTotal} supplierOptions={suppliers} onAdd={() => addExpenseLine("products")} onRemove={(index) => removeExpenseLine("products", index)} onChange={updateExpenseLine} multiplier={form.productMultiplier} onMultiplierChange={(val) => setForm(f => ({ ...f, productMultiplier: val }))} onReorder={moveExpenseLine} />
            <ExpenseTable title="Electrical Materials" hint="Examples: wires, cables, glands, lugs, ferrules, DIN rail, busbar supports." section="materials" lines={form.materials} total={totals.materialTotal} supplierOptions={suppliers} onAdd={() => addExpenseLine("materials")} onRemove={(index) => removeExpenseLine("materials", index)} onChange={updateExpenseLine} multiplier={form.materialMultiplier} onMultiplierChange={(val) => setForm(f => ({ ...f, materialMultiplier: val }))} onReorder={moveExpenseLine} />
            <MetalTable
              lines={form.metals}
              total={totals.metalTotal}
              copperRate={form.copperRate}
              aluminiumRate={form.aluminiumRate}
              onCopperRateChange={handleCopperRateChange}
              onAluminiumRateChange={handleAluminiumRateChange}
              supplierOptions={suppliers}
              onAdd={addMetalLine}
              onRemove={removeMetalLine}
              onChange={updateMetalLine}
              multiplier={form.metalMultiplier}
              onMultiplierChange={(val) => setForm(f => ({ ...f, metalMultiplier: val }))}
              onReorder={(index, dir) => moveExpenseLine("metals", index, dir)}
            />
            <LabourTable lines={form.labour} total={totals.labourTotal} supplierOptions={suppliers} onAdd={() => addExpenseLine("labour", emptyLabourLine())} onRemove={(index) => removeExpenseLine("labour", index, emptyLabourLine())} onChange={updateLabourLine} multiplier={form.labourMultiplier} onMultiplierChange={(val) => setForm(f => ({ ...f, labourMultiplier: val }))} onReorder={(index, dir) => moveExpenseLine("labour", index, dir)} />
            <ExpenseTable title="Transport" hint="Freight, vehicle rent, site travel and delivery charges." section="transport" lines={form.transport} total={totals.transportTotal} supplierOptions={suppliers} onAdd={() => addExpenseLine("transport", emptyExpenseLine("", "trip"))} onRemove={(index) => removeExpenseLine("transport", index, emptyExpenseLine("", "trip"))} onChange={updateExpenseLine} multiplier={form.transportMultiplier} onMultiplierChange={(val) => setForm(f => ({ ...f, transportMultiplier: val }))} onReorder={moveExpenseLine} />
            <ExpenseTable title="Stay" hint="Hotel, lodging, room rent, and other stay-related charges." section="stayExpenses" lines={form.stayExpenses} total={totals.stayTotal} supplierOptions={suppliers} onAdd={() => addExpenseLine("stayExpenses", emptyExpenseLine("Stay / lodging", "night"))} onRemove={(index) => removeExpenseLine("stayExpenses", index, emptyExpenseLine("Stay / lodging", "night"))} onChange={updateExpenseLine} multiplier={form.stayMultiplier} onMultiplierChange={(val) => setForm(f => ({ ...f, stayMultiplier: val }))} onReorder={moveExpenseLine} />
            <ExpenseTable title="Other Expenses" hint="Consumables, tools, testing charges, lodging, documentation or site overhead." section="otherExpenses" lines={form.otherExpenses} total={totals.otherTotal} supplierOptions={suppliers} onAdd={() => addExpenseLine("otherExpenses")} onRemove={(index) => removeExpenseLine("otherExpenses", index)} onChange={updateExpenseLine} multiplier={form.otherMultiplier} onMultiplierChange={(val) => setForm(f => ({ ...f, otherMultiplier: val }))} onReorder={moveExpenseLine} />

            <section className="rounded-lg border border-slate-200 bg-slate-50 p-5 shadow-sm">
              <h3 className="text-lg font-bold text-slate-950">Markup and Tax</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <Input label="Overhead %" name="overheadPercent" type="number" min="0" step="0.01" value={form.overheadPercent} onChange={updateField} />
                <Input label="Contingency %" name="contingencyPercent" type="number" min="0" step="0.01" value={form.contingencyPercent} onChange={updateField} />
                <Input label="Profit Margin %" name="profitPercent" type="number" min="0" step="0.01" value={form.profitPercent} onChange={updateField} />
                <Input label="GST %" name="gstPercent" type="number" min="0" step="0.01" value={form.gstPercent} onChange={updateField} />
              </div>
              <div className="mt-4 grid gap-2 text-sm md:grid-cols-2">
                <SummaryRow label="Overhead" value={formatCurrency(totals.overheadAmount)} />
                <SummaryRow label="Contingency" value={formatCurrency(totals.contingencyAmount)} />
                <SummaryRow label="Cost Before Profit" value={formatCurrency(totals.costBeforeProfit)} />
                <SummaryRow label="Profit" value={formatCurrency(totals.profitAmount)} />
                <SummaryRow label="Before GST" value={formatCurrency(totals.sellingPriceBeforeTax)} />
                <SummaryRow label="GST" value={formatCurrency(totals.gstAmount)} />
                <SummaryRow label="Final Project Value" value={formatCurrency(totals.finalProjectValue)} strong />
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <label className="mb-2 block text-sm font-semibold text-slate-700">Notes</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={updateField}
                rows={5}
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500"
                placeholder="Mention assumptions, excluded work, special site conditions or rate validity."
              />
            </section>

            <section className="rounded-[1.5rem] border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-5 shadow-[0_18px_40px_rgba(16,185,129,0.12)]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-xl font-bold text-emerald-950">Cost Summary</h3>
                  <p className="mt-1 text-sm text-emerald-700">Full estimate breakdown with overheads included.</p>
                </div>
                <button type="submit" disabled={saving} className="rounded-lg bg-emerald-700 px-6 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-60">
                  {saving ? "Saving..." : editingId ? "Update Project Estimate" : "Save Project Estimate"}
                </button>
              </div>
              <div className="mt-4 grid gap-2 text-sm md:grid-cols-2 xl:grid-cols-3">
                <SummaryRow label={`Products (1 Unit)`} value={formatCurrency(totals.productBaseTotal)} />
                <SummaryRow label={`Products Total${form.productMultiplier > 1 ? ` (x${form.productMultiplier})` : ''}`} value={formatCurrency(totals.productTotal)} strong />
                <SummaryRow label={`Materials (1 Unit)`} value={formatCurrency(totals.materialBaseTotal)} />
                <SummaryRow label={`Materials Total${form.materialMultiplier > 1 ? ` (x${form.materialMultiplier})` : ''}`} value={formatCurrency(totals.materialTotal)} strong />
                <SummaryRow label={`Copper + Aluminium (1 Unit)`} value={formatCurrency(totals.metalBaseTotal)} />
                <SummaryRow label={`Copper + Aluminium Total${form.metalMultiplier > 1 ? ` (x${form.metalMultiplier})` : ''}`} value={formatCurrency(totals.metalTotal)} strong />
                <SummaryRow label={`Labour Total${form.labourMultiplier > 1 ? ` (x${form.labourMultiplier})` : ''}`} value={formatCurrency(totals.labourTotal)} />
                <SummaryRow label={`Transport Total${form.transportMultiplier > 1 ? ` (x${form.transportMultiplier})` : ''}`} value={formatCurrency(totals.transportTotal)} />
                <SummaryRow label={`Stay Total${form.stayMultiplier > 1 ? ` (x${form.stayMultiplier})` : ''}`} value={formatCurrency(totals.stayTotal)} />
                <SummaryRow label={`Other Total${form.otherMultiplier > 1 ? ` (x${form.otherMultiplier})` : ''}`} value={formatCurrency(totals.otherTotal)} />
                <SummaryRow label="Direct Cost (Total × Qty of all sections)" value={formatCurrency(totals.directCost)} strong />
                <SummaryRow label="Overhead" value={formatCurrency(totals.overheadAmount)} />
                <SummaryRow label="Contingency" value={formatCurrency(totals.contingencyAmount)} />
                <SummaryRow label="Cost Before Profit" value={formatCurrency(totals.costBeforeProfit)} />
                <SummaryRow label="Profit" value={formatCurrency(totals.profitAmount)} />
                <SummaryRow label="Before GST" value={formatCurrency(totals.sellingPriceBeforeTax)} />
                <SummaryRow label="GST" value={formatCurrency(totals.gstAmount)} />
                <SummaryRow label="Final Project Value" value={formatCurrency(totals.finalProjectValue)} strong />
              </div>
            </section>
          </div>
        </form>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-950">Project Estimate History</h2>
          </div>
          <div className="flex w-full flex-col gap-3 sm:flex-row lg:max-w-md">
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search estimates..." className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none" />
            <button type="button" onClick={() => loadData(search)} className="rounded-lg bg-slate-950 px-5 py-3 text-sm font-semibold text-white">
              Search
            </button>
          </div>
        </div>

        <div className="mt-5 space-y-3 md:hidden">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">Loading project estimates...</div>
          ) : expenses.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-500">No project estimates found.</div>
          ) : expenses.map((expense) => (
            <article key={expense._id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{formatDate(expense.estimateDate)}</p>
                  <h3 className="mt-1 break-words text-base font-bold text-slate-950">{expense.projectName || expense.estimateNo || "Project Estimate"}</h3>
                  <p className="mt-1 text-xs text-slate-500">{projectTypes.find(([value]) => value === expense.projectType)?.[1] || expense.projectType || "Project"}</p>
                </div>
                <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-bold uppercase text-emerald-800">{expense.status}</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Estimate</p>
                  <p className="mt-1 font-semibold text-slate-800">{expense.estimateNo || "-"}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Customer</p>
                  <p className="mt-1 truncate font-semibold text-slate-800">{expense.customerName || "-"}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Direct Cost</p>
                  <p className="mt-1 font-semibold text-slate-800">{formatCurrency(expense.directCost)}</p>
                </div>
                <div className="rounded-xl bg-emerald-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Final Value</p>
                  <p className="mt-1 font-bold text-emerald-800">{formatCurrency(expense.finalProjectValue)}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={() => openExpensePdf(expense)} className="rounded-xl border border-sky-300 px-4 py-2.5 text-xs font-semibold uppercase text-sky-800">PDF</button>
                <button type="button" onClick={() => downloadExpensePdf(expense)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-xs font-semibold uppercase text-slate-700">Download PDF</button>
                <button type="button" onClick={() => editExpense(expense)} className="rounded-xl border border-emerald-300 px-4 py-2.5 text-xs font-semibold uppercase text-emerald-800">Edit</button>
                <button type="button" onClick={() => duplicateExpense(expense)} className="rounded-xl border border-violet-300 px-4 py-2.5 text-xs font-semibold uppercase text-violet-800">Duplicate</button>
                <button type="button" onClick={() => deleteExpense(expense)} className="rounded-xl border border-red-200 px-4 py-2.5 text-xs font-semibold uppercase text-red-700">Delete</button>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-5 hidden overflow-x-auto md:block">
          <table className="w-full min-w-[980px]">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Estimate No</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3 text-right">Direct Cost</th>
                <th className="px-4 py-3 text-right">Final Value</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500">Loading project estimates...</td></tr>
              ) : expenses.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500">No project estimates found.</td></tr>
              ) : expenses.map((expense) => (
                <tr key={expense._id} className="border-t border-slate-200 align-top">
                  <td className="px-4 py-3 text-sm text-slate-700">{formatDate(expense.estimateDate)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">{expense.estimateNo}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">{expense.projectName}</p>
                    <p className="text-xs text-slate-500">{projectTypes.find(([value]) => value === expense.projectType)?.[1] || expense.projectType || "Project"}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{expense.customerName || "-"}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-slate-900">{formatCurrency(expense.directCost)}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-700">{formatCurrency(expense.finalProjectValue)}</td>
                  <td className="px-4 py-3"><span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase text-emerald-800">{expense.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => openExpensePdf(expense)} className="rounded-lg border border-sky-300 px-4 py-2 text-xs font-semibold uppercase text-sky-800">PDF</button>
                      <button type="button" onClick={() => downloadExpensePdf(expense)} className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-semibold uppercase text-slate-700">Download PDF</button>
                      <button type="button" onClick={() => editExpense(expense)} className="rounded-lg border border-emerald-300 px-4 py-2 text-xs font-semibold uppercase text-emerald-800">Edit</button>
                      <button type="button" onClick={() => duplicateExpense(expense)} className="rounded-lg border border-violet-300 px-4 py-2 text-xs font-semibold uppercase text-violet-800">Duplicate</button>
                      <button type="button" onClick={() => deleteExpense(expense)} className="rounded-lg border border-red-200 px-4 py-2 text-xs font-semibold uppercase text-red-700">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function normalizeExpenseLineForForm(line) {
  return {
    name: line.name || "",
    supplierName: line.supplierName || "",
    description: line.description || "",
    remarks: line.remarks || "",
    quantity: String(line.quantity ?? ""),
    unit: formatUnit(line.unit),
    unitRate: String(line.unitRate ?? ""),
    amount: String(line.amount ?? ""),
    overheadType: line.overheadType || "none",
    overheadValue: String(line.overheadValue ?? ""),
    overheadAmount: String(line.overheadAmount ?? ""),
    lineTotal: String(line.lineTotal ?? "")
  };
}

function normalizeMetalLineForForm(line) {
  return {
    metal: line.metal || "copper",
    supplierName: line.supplierName || "",
    remarks: line.remarks || "",
    quantity: String(line.quantity ?? 1),
    runs: String(line.runs ?? 1),
    widthMm: String(line.widthMm ?? ""),
    thicknessMm: String(line.thicknessMm ?? ""),
    lengthMm: String(line.lengthMm ?? ""),
    kgUsed: String(line.kgUsed ?? ""),
    ratePerKg: String(line.ratePerKg ?? ""),
    amount: String(line.amount ?? ""),
    overheadType: line.overheadType || "none",
    overheadValue: String(line.overheadValue ?? ""),
    overheadAmount: String(line.overheadAmount ?? ""),
    lineTotal: String(line.lineTotal ?? "")
  };
}

function normalizeLabourLineForForm(line) {
  return {
    role: line.role || "",
    supplierName: line.supplierName || "",
    remarks: line.remarks || "",
    workers: String(line.workers ?? ""),
    days: String(line.days ?? ""),
    ratePerDay: String(line.ratePerDay ?? ""),
    amount: String(line.amount ?? "")
  };
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">{label}</label>
      <input {...props} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500" />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">{label}</label>
      <select {...props} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-emerald-500">
        {options.map(([value, labelText]) => <option key={value} value={value}>{labelText}</option>)}
      </select>
    </div>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className={`flex items-center justify-between rounded-lg bg-white px-4 py-3 ${strong ? "font-bold text-slate-950" : "text-slate-700"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ExpenseTable({ title, hint, section, lines, total, supplierOptions, onAdd, onRemove, onChange, multiplier, onMultiplierChange, onReorder }) {
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= lines.length) {
      setEditingIndex(lines.length ? lines.length - 1 : null);
    }
  }, [editingIndex, lines.length]);

  const handleAdd = () => {
    onAdd();
    setEditingIndex(lines.length);
  };

  const handleRemove = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex((current) => (current === null ? null : Math.max(0, current - 1)));
    }
    onRemove(index);
  };

  const cellClass = "border border-slate-300 px-2 py-1 align-middle text-[12px] leading-4 text-slate-900";
  const headClass = "border border-slate-300 bg-slate-100 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-700";
  const inputClass = "h-7 w-full border-0 bg-transparent px-1 text-[12px] leading-4 outline-none focus:bg-slate-50 focus:ring-0";
  const selectClass = "h-7 w-full border-0 bg-transparent px-1 text-[12px] leading-4 outline-none focus:bg-slate-50 focus:ring-0";

  const displayText = (value) => {
    const text = String(value ?? "").trim();
    return text ? text : "-";
  };

  const displayNumber = (value, digits = 2) => {
    if (value === "" || value === null || value === undefined) return "-";
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return "-";
    return numberValue.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits
    });
  };

  const displayMoney = (value) => formatCurrency(value);

  const renderDisplayCell = (value, numeric = false) => (
    <div className={`${numeric ? "justify-end tabular-nums" : "justify-start"} flex h-7 items-center overflow-hidden whitespace-nowrap text-ellipsis`}>
      {value}
    </div>
  );

  const renderInputCell = (props) => (
    <input
      {...props}
      className={`${inputClass} ${props.className || ""}`.trim()}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
    />
  );

  const renderSupplierSelect = (value, onValueChange) => (
    <select
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      className={selectClass}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
    >
      <option value="">Select supplier</option>
      {supplierOptions.map((supplier) => {
        const name = supplier.companyName || supplier.name || "";
        return name ? <option key={supplier._id} value={name}>{name}</option> : null;
      })}
    </select>
  );

  return (
    <section className="border border-slate-300 bg-white">
      <div className="flex items-center justify-between border-b border-slate-300 bg-slate-50 px-3 py-2">
        <div className="min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-900">{title}</h3>
          <p className="text-[11px] text-slate-500">{hint}</p>
        </div>
        <button type="button" onClick={handleAdd} className="border border-slate-800 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white">
          + Add Row
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse table-fixed min-w-[1180px]">
          <thead>
            <tr>
              <th className={headClass} style={{ width: 44 }}>#</th>
              <th className={headClass} style={{ width: 190 }}>Item</th>
              <th className={headClass} style={{ width: 190 }}>Supplier</th>
              <th className={headClass} style={{ width: 220 }}>Description</th>
              <th className={headClass} style={{ width: 200 }}>Remarks</th>
              <th className={headClass} style={{ width: 86 }}>Qty</th>
              <th className={headClass} style={{ width: 80 }}>Unit</th>
              <th className={headClass} style={{ width: 110 }}>Rate</th>
              <th className={headClass} style={{ width: 120 }}>Amount</th>
              <th className={headClass} style={{ width: 120 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const isEditing = editingIndex === index;
              return (
                <tr
                  key={`${section}-${index}`}
                  className={`${isEditing ? "bg-amber-50" : "bg-white"} cursor-pointer hover:bg-slate-50`}
                  onClick={() => setEditingIndex(index)}
                >
                  <td className={`${cellClass} text-center tabular-nums`}>{index + 1}</td>
                  <td className={cellClass}>
                    {isEditing ? renderInputCell({ type: "text", value: line.name, onChange: (event) => onChange(section, index, "name", event.target.value) }) : renderDisplayCell(displayText(line.name))}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? renderSupplierSelect(line.supplierName, (value) => onChange(section, index, "supplierName", value)) : renderDisplayCell(displayText(line.supplierName))}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? renderInputCell({ type: "text", value: line.description, onChange: (event) => onChange(section, index, "description", event.target.value) }) : renderDisplayCell(displayText(line.description))}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? renderInputCell({ type: "text", value: line.remarks, onChange: (event) => onChange(section, index, "remarks", event.target.value) }) : renderDisplayCell(displayText(line.remarks))}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? renderInputCell({ type: "number", min: "0", step: "0.01", value: line.quantity, onChange: (event) => onChange(section, index, "quantity", event.target.value), className: "text-right tabular-nums" }) : renderDisplayCell(displayNumber(line.quantity, 2), true)}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? renderInputCell({ type: "text", value: line.unit, onChange: (event) => onChange(section, index, "unit", event.target.value) }) : renderDisplayCell(displayText(line.unit))}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? renderInputCell({ type: "number", min: "0", step: "0.01", value: line.unitRate, onChange: (event) => onChange(section, index, "unitRate", event.target.value), className: "text-right tabular-nums" }) : renderDisplayCell(displayMoney(line.unitRate), true)}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? renderInputCell({ type: "number", min: "0", step: "0.01", value: line.amount, onChange: (event) => onChange(section, index, "amount", event.target.value), className: "text-right tabular-nums" }) : renderDisplayCell(displayMoney(line.amount), true)}
                  </td>
                  <td className={`${cellClass} whitespace-nowrap`}>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          onReorder(section, index, "up");
                        }}
                        className="border border-slate-300 px-1.5 py-1 text-[11px] disabled:opacity-30 hover:bg-slate-100 font-semibold"
                        title="Move Up"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={index === lines.length - 1}
                        onClick={(event) => {
                          event.stopPropagation();
                          onReorder(section, index, "down");
                        }}
                        className="border border-slate-300 px-1.5 py-1 text-[11px] disabled:opacity-30 hover:bg-slate-100 font-semibold"
                        title="Move Down"
                      >
                        ▼
                      </button>
                      {isEditing ? (
                        <button type="button" onClick={(event) => { event.stopPropagation(); setEditingIndex(null); }} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">Done</button>
                      ) : (
                        <button type="button" onClick={(event) => { event.stopPropagation(); setEditingIndex(index); }} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">Edit</button>
                      )}
                      <button type="button" onClick={(event) => { event.stopPropagation(); handleRemove(index); }} className="border border-red-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-red-700">Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {/* Total (1 Product) */}
            <tr className="bg-slate-50 border-t border-slate-300">
              <td className="border border-slate-300 px-2 py-1" colSpan={7} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-600">
                Total (1 Product)
              </td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold tabular-nums text-slate-700">
                {formatCurrency(lines.reduce((sum, line) => sum + computeLineAmount(line), 0))}
              </td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
            {/* Multy No. */}
            <tr className="bg-slate-50">
              <td className="border border-slate-300 px-2 py-1" colSpan={7} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-600">
                Product Qty
              </td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold tabular-nums text-slate-700">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={multiplier || 1}
                  onChange={(e) => onMultiplierChange(e.target.value)}
                  className="h-7 w-20 border border-slate-300 rounded px-1.5 text-right text-[12px] font-semibold outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
            {/* Grand Total */}
            <tr className="bg-emerald-50 font-bold text-emerald-900">
              <td className="border border-slate-300 px-2 py-1" colSpan={7} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] uppercase tracking-wide">
                Grand Total
              </td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] tabular-nums">
                {formatCurrency(total)}
              </td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function MetalTable({
  lines,
  total,
  copperRate,
  aluminiumRate,
  onCopperRateChange,
  onAluminiumRateChange,
  supplierOptions,
  onAdd,
  onRemove,
  onChange,
  multiplier,
  onMultiplierChange,
  onReorder
}) {
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= lines.length) {
      setEditingIndex(lines.length ? lines.length - 1 : null);
    }
  }, [editingIndex, lines.length]);

  const handleAdd = () => {
    onAdd();
    setEditingIndex(lines.length);
  };

  const handleRemove = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex((current) => (current === null ? null : Math.max(0, current - 1)));
    }
    onRemove(index);
  };

  const cellClass = "border border-slate-300 px-2 py-1 align-middle text-[12px] leading-4 text-slate-900";
  const headClass = "border border-slate-300 bg-slate-100 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-700";
  const groupHeadClass = "border border-slate-300 bg-slate-200 px-2 py-1.5 text-center text-[11px] font-bold uppercase tracking-wide text-slate-700";
  const inputClass = "h-7 w-full border-0 bg-transparent px-1 text-[12px] leading-4 outline-none focus:bg-slate-50 focus:ring-0";
  const selectClass = "h-7 w-full border-0 bg-transparent px-1 text-[12px] leading-4 outline-none focus:bg-slate-50 focus:ring-0";

  const displayText = (value) => {
    const text = String(value ?? "").trim();
    return text ? text : "-";
  };

  const displayNumber = (value, digits = 2) => {
    if (value === "" || value === null || value === undefined) return "-";
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return "-";
    return numberValue.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits
    });
  };

  const displayMoney = (value) => formatCurrency(value);

  const renderDisplayCell = (value, numeric = false, className = "") => (
    <div className={`${numeric ? "justify-end tabular-nums" : "justify-start"} flex h-7 items-center overflow-hidden whitespace-nowrap text-ellipsis ${className}`}>
      {value}
    </div>
  );

  const renderInputCell = (props) => (
    <input
      {...props}
      className={`${inputClass} ${props.className || ""}`.trim()}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
    />
  );

  return (
    <section className="border border-slate-300 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-300 bg-slate-50 px-3 py-2">
        <div className="min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-900">Copper and Aluminium</h3>
          <p className="text-[11px] text-slate-500">Compact ERP-style costing grid. Click a row to edit inline.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Top fixed price for Copper */}
          <div className="flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2.5 py-1 shadow-sm">
            <span className="text-[11px] font-bold uppercase tracking-wide text-amber-900">Copper Rate/Kg:</span>
            <span className="text-xs font-bold text-amber-950">₹</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={copperRate ?? ""}
              onChange={(e) => onCopperRateChange(e.target.value)}
              placeholder="1350"
              className="h-6 w-24 rounded border border-amber-300 bg-white px-1.5 text-right text-xs font-bold text-slate-900 outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-500"
            />
          </div>

          {/* Top fixed price for Aluminium */}
          <div className="flex items-center gap-1.5 rounded border border-slate-300 bg-slate-100 px-2.5 py-1 shadow-sm">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-700">Aluminium Rate/Kg:</span>
            <span className="text-xs font-bold text-slate-900">₹</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={aluminiumRate ?? ""}
              onChange={(e) => onAluminiumRateChange(e.target.value)}
              placeholder="400"
              className="h-6 w-24 rounded border border-slate-300 bg-white px-1.5 text-right text-xs font-bold text-slate-900 outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
            />
          </div>

          <button type="button" onClick={handleAdd} className="rounded border border-slate-800 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-900 transition">
            + Add Metal Row
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[1400px] w-full border-collapse table-fixed">
          <thead>
            <tr>
              <th className={headClass} rowSpan={2} style={{ width: 44 }}>#</th>
              <th className={headClass} rowSpan={2} style={{ width: 96 }}>Metal</th>
              <th className={headClass} rowSpan={2} style={{ width: 190 }}>Supplier</th>
              <th className={headClass} rowSpan={2} style={{ width: 210 }}>Remarks</th>
              <th className={groupHeadClass} colSpan={5}>Dimensions</th>
              <th className={headClass} rowSpan={2} style={{ width: 100 }}>Kg Used</th>
              <th className={headClass} rowSpan={2} style={{ width: 110 }}>Rate/Kg</th>
              <th className={headClass} rowSpan={2} style={{ width: 120 }}>Amount</th>
              <th className={headClass} rowSpan={2} style={{ width: 120 }}>Action</th>
            </tr>
            <tr>
              <th className={headClass} style={{ width: 68 }}>Qty</th>
              <th className={headClass} style={{ width: 68 }}>Runs</th>
              <th className={headClass} style={{ width: 88 }}>Width</th>
              <th className={headClass} style={{ width: 88 }}>Thickness</th>
              <th className={headClass} style={{ width: 88 }}>Length</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const isEditing = editingIndex === index;
              const metalLabel = line.metal === "aluminium" ? "Aluminium" : "Copper";
              const supplierLabel = displayText(line.supplierName);
              const remarksLabel = displayText(line.remarks);
              const qtyLabel = displayNumber(line.quantity, 0);
              const runsLabel = displayNumber(line.runs ?? 1, 0);
              const widthLabel = displayNumber(line.widthMm, 2);
              const thicknessLabel = displayNumber(line.thicknessMm, 2);
              const lengthLabel = displayNumber(line.lengthMm, 2);
              const kgUsedLabel = displayNumber(line.kgUsed, 3);
              const rateLabel = displayMoney(line.ratePerKg);
              const amountLabel = displayMoney(line.amount);

              return (
                <tr
                  key={`metal-${index}`}
                  className={`${isEditing ? "bg-amber-50" : "bg-white"} cursor-pointer hover:bg-slate-50`}
                  onClick={() => setEditingIndex(index)}
                >
                  <td className={`${cellClass} text-center tabular-nums`}>{index + 1}</td>
                  <td className={cellClass}>
                    {isEditing ? (
                      <select
                        value={line.metal}
                        onChange={(event) => onChange(index, "metal", event.target.value)}
                        className={`${selectClass} capitalize`}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                      >
                        <option value="copper">Copper</option>
                        <option value="aluminium">Aluminium</option>
                      </select>
                    ) : (
                      renderDisplayCell(metalLabel)
                    )}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? (
                      <select
                        value={line.supplierName}
                        onChange={(event) => onChange(index, "supplierName", event.target.value)}
                        className={selectClass}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                      >
                        <option value="">Select supplier</option>
                        {supplierOptions.map((supplier) => {
                          const name = supplier.companyName || supplier.name || "";
                          return name ? <option key={supplier._id} value={name}>{name}</option> : null;
                        })}
                      </select>
                    ) : (
                      renderDisplayCell(supplierLabel)
                    )}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? (
                      renderInputCell({
                        type: "text",
                        value: line.remarks,
                        onChange: (event) => onChange(index, "remarks", event.target.value),
                        className: "text-left"
                      })
                    ) : (
                      renderDisplayCell(remarksLabel, false)
                    )}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "1",
                        value: line.quantity,
                        onChange: (event) => onChange(index, "quantity", event.target.value),
                        className: "text-right tabular-nums"
                      })
                    ) : (
                      renderDisplayCell(qtyLabel, true)
                    )}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "1",
                        value: line.runs ?? 1,
                        onChange: (event) => onChange(index, "runs", event.target.value),
                        className: "text-right tabular-nums",
                        placeholder: "1"
                      })
                    ) : (
                      renderDisplayCell(runsLabel, true)
                    )}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "0.01",
                        value: line.widthMm,
                        onChange: (event) => onChange(index, "widthMm", event.target.value),
                        className: "text-right tabular-nums"
                      })
                    ) : (
                      renderDisplayCell(widthLabel, true)
                    )}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "0.01",
                        value: line.thicknessMm,
                        onChange: (event) => onChange(index, "thicknessMm", event.target.value),
                        className: "text-right tabular-nums"
                      })
                    ) : (
                      renderDisplayCell(thicknessLabel, true)
                    )}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "0.01",
                        value: line.lengthMm,
                        onChange: (event) => onChange(index, "lengthMm", event.target.value),
                        className: "text-right tabular-nums"
                      })
                    ) : (
                      renderDisplayCell(lengthLabel, true)
                    )}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "0.001",
                        value: line.kgUsed,
                        onChange: (event) => onChange(index, "kgUsed", event.target.value),
                        className: "text-right tabular-nums"
                      })
                    ) : (
                      renderDisplayCell(kgUsedLabel, true)
                    )}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "0.01",
                        value: line.ratePerKg,
                        onChange: (event) => onChange(index, "ratePerKg", event.target.value),
                        className: "text-right tabular-nums"
                      })
                    ) : (
                      renderDisplayCell(rateLabel, true)
                    )}
                  </td>
                  <td className={`${cellClass} text-right font-semibold`}>
                    {isEditing ? (
                      renderInputCell({
                        type: "number",
                        min: "0",
                        step: "0.01",
                        value: line.amount,
                        onChange: (event) => onChange(index, "amount", event.target.value),
                        className: "text-right tabular-nums font-semibold"
                      })
                    ) : (
                      renderDisplayCell(amountLabel, true, "font-semibold")
                    )}
                  </td>
                  <td className={`${cellClass} whitespace-nowrap`}>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          onReorder(index, "up");
                        }}
                        className="border border-slate-300 px-1.5 py-1 text-[11px] disabled:opacity-30 hover:bg-slate-100 font-semibold"
                        title="Move Up"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={index === lines.length - 1}
                        onClick={(event) => {
                          event.stopPropagation();
                          onReorder(index, "down");
                        }}
                        className="border border-slate-300 px-1.5 py-1 text-[11px] disabled:opacity-30 hover:bg-slate-100 font-semibold"
                        title="Move Down"
                      >
                        ▼
                      </button>
                      {isEditing ? (
                        <button type="button" onClick={(event) => { event.stopPropagation(); setEditingIndex(null); }} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                          Done
                        </button>
                      ) : (
                        <button type="button" onClick={(event) => { event.stopPropagation(); setEditingIndex(index); }} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                          Edit
                        </button>
                      )}
                      <button type="button" onClick={(event) => { event.stopPropagation(); handleRemove(index); }} className="border border-red-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-red-700">
                        Del
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {/* Total (1 Product) */}
            <tr className="bg-slate-50 border-t border-slate-300">
              <td className="border border-slate-300 px-2 py-1" colSpan={10} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-600">Total (1 Product)</td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold tabular-nums text-slate-700">
                {formatCurrency(lines.reduce((sum, line) => sum + computeMetalAmount(line), 0))}
              </td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
            {/* Multy No. */}
            <tr className="bg-slate-50">
              <td className="border border-slate-300 px-2 py-1" colSpan={10} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-600">Product Qty</td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold tabular-nums text-slate-700">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={multiplier || 1}
                  onChange={(e) => onMultiplierChange(e.target.value)}
                  className="h-7 w-20 border border-slate-300 rounded px-1.5 text-right text-[12px] font-semibold outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
            {/* Grand Total */}
            <tr className="bg-emerald-50 font-bold text-emerald-900">
              <td className="border border-slate-300 px-2 py-1" colSpan={10} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] uppercase tracking-wide">Grand Total</td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] tabular-nums">{formatCurrency(total)}</td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function LabourTable({ lines, total, supplierOptions, onAdd, onRemove, onChange, multiplier, onMultiplierChange, onReorder }) {
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= lines.length) {
      setEditingIndex(lines.length ? lines.length - 1 : null);
    }
  }, [editingIndex, lines.length]);

  const handleAdd = () => {
    onAdd();
    setEditingIndex(lines.length);
  };

  const handleRemove = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex((current) => (current === null ? null : Math.max(0, current - 1)));
    }
    onRemove(index);
  };

  const cellClass = "border border-slate-300 px-2 py-1 align-middle text-[12px] leading-4 text-slate-900";
  const headClass = "border border-slate-300 bg-slate-100 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-700";
  const inputClass = "h-7 w-full border-0 bg-transparent px-1 text-[12px] leading-4 outline-none focus:bg-slate-50 focus:ring-0";
  const selectClass = "h-7 w-full border-0 bg-transparent px-1 text-[12px] leading-4 outline-none focus:bg-slate-50 focus:ring-0";

  const displayText = (value) => {
    const text = String(value ?? "").trim();
    return text ? text : "-";
  };

  const displayNumber = (value, digits = 2) => {
    if (value === "" || value === null || value === undefined) return "-";
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return "-";
    return numberValue.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits
    });
  };

  const renderDisplayCell = (value, numeric = false) => (
    <div className={`${numeric ? "justify-end tabular-nums" : "justify-start"} flex h-7 items-center overflow-hidden whitespace-nowrap text-ellipsis`}>
      {value}
    </div>
  );

  const renderInputCell = (props) => (
    <input
      {...props}
      className={`${inputClass} ${props.className || ""}`.trim()}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
    />
  );

  const renderSupplierSelect = (value, onValueChange) => (
    <select
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      className={selectClass}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
    >
      <option value="">Select supplier</option>
      {supplierOptions.map((supplier) => {
        const name = supplier.companyName || supplier.name || "";
        return name ? <option key={supplier._id} value={name}>{name}</option> : null;
      })}
    </select>
  );

  return (
    <section className="border border-slate-300 bg-white">
      <div className="flex items-center justify-between border-b border-slate-300 bg-slate-50 px-3 py-2">
        <div className="min-w-0">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-900">Labour</h3>
          <p className="text-[11px] text-slate-500">Compact ERP-style costing grid. Click a row to edit inline.</p>
        </div>
        <button type="button" onClick={handleAdd} className="border border-slate-800 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white">
          + Add Labour
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] table-fixed border-collapse">
          <thead>
            <tr>
              <th className={headClass} style={{ width: 44 }}>#</th>
              <th className={headClass} style={{ width: 220 }}>Work</th>
              <th className={headClass} style={{ width: 190 }}>Supplier</th>
              <th className={headClass} style={{ width: 260 }}>Remarks</th>
              <th className={headClass} style={{ width: 100 }}>Workers</th>
              <th className={headClass} style={{ width: 100 }}>Days</th>
              <th className={headClass} style={{ width: 120 }}>Rate / Day</th>
              <th className={headClass} style={{ width: 120 }}>Amount</th>
              <th className={headClass} style={{ width: 120 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const isEditing = editingIndex === index;
              return (
                <tr
                  key={`labour-${index}`}
                  className={`${isEditing ? "bg-amber-50" : "bg-white"} cursor-pointer hover:bg-slate-50`}
                  onClick={() => setEditingIndex(index)}
                >
                  <td className={`${cellClass} text-center tabular-nums`}>{index + 1}</td>
                  <td className={cellClass}>
                    {isEditing ? renderInputCell({ type: "text", value: line.role, onChange: (event) => onChange(index, "role", event.target.value) }) : renderDisplayCell(displayText(line.role))}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? renderSupplierSelect(line.supplierName, (value) => onChange(index, "supplierName", value)) : renderDisplayCell(displayText(line.supplierName))}
                  </td>
                  <td className={cellClass}>
                    {isEditing ? renderInputCell({ type: "text", value: line.remarks, onChange: (event) => onChange(index, "remarks", event.target.value) }) : renderDisplayCell(displayText(line.remarks))}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? renderInputCell({ type: "number", min: "0", step: "0.01", value: line.workers, onChange: (event) => onChange(index, "workers", event.target.value), className: "text-right tabular-nums" }) : renderDisplayCell(displayNumber(line.workers, 2), true)}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? renderInputCell({ type: "number", min: "0", step: "0.01", value: line.days, onChange: (event) => onChange(index, "days", event.target.value), className: "text-right tabular-nums" }) : renderDisplayCell(displayNumber(line.days, 2), true)}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? renderInputCell({ type: "number", min: "0", step: "0.01", value: line.ratePerDay, onChange: (event) => onChange(index, "ratePerDay", event.target.value), className: "text-right tabular-nums" }) : renderDisplayCell(formatCurrency(line.ratePerDay), true)}
                  </td>
                  <td className={`${cellClass} text-right`}>
                    {isEditing ? renderInputCell({ type: "number", min: "0", step: "0.01", value: line.amount, onChange: (event) => onChange(index, "amount", event.target.value), className: "text-right tabular-nums" }) : renderDisplayCell(formatCurrency(line.amount), true)}
                  </td>
                  <td className={`${cellClass} whitespace-nowrap`}>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          onReorder(index, "up");
                        }}
                        className="border border-slate-300 px-1.5 py-1 text-[11px] disabled:opacity-30 hover:bg-slate-100 font-semibold"
                        title="Move Up"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={index === lines.length - 1}
                        onClick={(event) => {
                          event.stopPropagation();
                          onReorder(index, "down");
                        }}
                        className="border border-slate-300 px-1.5 py-1 text-[11px] disabled:opacity-30 hover:bg-slate-100 font-semibold"
                        title="Move Down"
                      >
                        ▼
                      </button>
                      {isEditing ? (
                        <button type="button" onClick={(event) => { event.stopPropagation(); setEditingIndex(null); }} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">Done</button>
                      ) : (
                        <button type="button" onClick={(event) => { event.stopPropagation(); setEditingIndex(index); }} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">Edit</button>
                      )}
                      <button type="button" onClick={(event) => { event.stopPropagation(); handleRemove(index); }} className="border border-red-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-red-700">Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {/* Total (1 Product) */}
            <tr className="bg-slate-50 border-t border-slate-300">
              <td className="border border-slate-300 px-2 py-1" colSpan={6} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-600">Total (1 Product)</td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold tabular-nums text-slate-700">
                {formatCurrency(lines.reduce((sum, line) => sum + computeLabourAmount(line), 0))}
              </td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
            {/* Multy No. */}
            <tr className="bg-slate-50">
              <td className="border border-slate-300 px-2 py-1" colSpan={6} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold uppercase tracking-wide text-slate-600">Product Qty</td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] font-semibold tabular-nums text-slate-700">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={multiplier || 1}
                  onChange={(e) => onMultiplierChange(e.target.value)}
                  className="h-7 w-20 border border-slate-300 rounded px-1.5 text-right text-[12px] font-semibold outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
            {/* Grand Total */}
            <tr className="bg-emerald-50 font-bold text-emerald-900">
              <td className="border border-slate-300 px-2 py-1" colSpan={6} />
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] uppercase tracking-wide">Grand Total</td>
              <td className="border border-slate-300 px-2 py-1 text-right text-[12px] tabular-nums">{formatCurrency(total)}</td>
              <td className="border border-slate-300 px-2 py-1" />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}


